import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, Check } from "lucide-react";
import { api, imgUrl } from "@/lib/api";
import { categoryName } from "@/lib/constants";
import { Reveal } from "@/components/Reveal";

export default function ProductDetail() {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setProduct(null);
    setNotFound(false);
    api.get(`/products/${id}`)
      .then((r) => setProduct(r.data))
      .catch(() => setNotFound(true));
  }, [id]);

  if (notFound) {
    return (
      <main className="pt-40 pb-32 px-6 text-center min-h-screen">
        <p className="font-serif text-3xl text-ink">This piece has moved on.</p>
        <Link to="/catalogue" className="mt-6 inline-block text-gold-brass gold-underline-btn text-sm font-medium">
          Back to the catalogue
        </Link>
      </main>
    );
  }

  if (!product) {
    return <main className="pt-40 pb-32 min-h-screen" />;
  }

  return (
    <main data-testid="product-detail-modal" className="pt-32 pb-24 px-6 lg:px-12 max-w-7xl mx-auto min-h-screen">
      <Reveal>
        <Link to="/catalogue" className="inline-flex items-center gap-2 text-sm text-ink-muted hover:text-ink transition-colors duration-300">
          <ArrowLeft size={15} /> Back to catalogue
        </Link>
      </Reveal>

      <div className="mt-10 grid lg:grid-cols-2 gap-12 lg:gap-20">
        <Reveal className="relative">
          <div className="absolute -inset-3 border border-gold/25" />
          <div className="relative overflow-hidden bg-cream-dark">
            <img src={imgUrl(product.image)} alt={product.name} className="w-full aspect-[4/5] object-cover" />
            {product.featured && (
              <span className="absolute top-5 left-5 bg-ink/85 text-gold font-mono text-[10px] uppercase tracking-[0.2em] px-3 py-1.5 backdrop-blur">
                Featured
              </span>
            )}
          </div>
        </Reveal>

        <Reveal delay={0.15} className="flex flex-col justify-center">
          <p className="eyebrow">{categoryName(product.category)}</p>
          <h1 className="mt-4 font-serif font-light text-4xl sm:text-5xl text-ink tracking-tight text-balance">
            {product.name}
          </h1>
          <p className="mt-4 font-serif italic text-lg text-gold-brass">{product.tagline}</p>
          <p className="mt-6 text-base text-ink-muted leading-relaxed">{product.description}</p>

          <div className="mt-8 flex items-center gap-4">
            <span className="border border-gold/30 bg-white px-4 py-2 font-mono text-[11px] uppercase tracking-[0.15em] text-ink/70">
              Minimum order · {product.moq} units
            </span>
          </div>

          {product.customization?.length > 0 && (
            <div className="mt-10">
              <p className="eyebrow mb-4">Customization options</p>
              <ul className="space-y-3">
                {product.customization.map((c) => (
                  <li key={c} className="flex items-start gap-3 text-sm text-ink/80">
                    <Check size={16} className="text-gold-brass mt-0.5 shrink-0" />
                    {c}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="mt-12 flex flex-wrap items-center gap-6">
            <Link
              to={`/enquire?product=${product.id}`}
              data-testid="product-card-request-quote-button"
              className="inline-flex items-center gap-2 bg-ink text-cream px-9 py-4 text-sm tracking-wide hover:bg-gold-brass transition-colors duration-400 group"
            >
              Request a Quote
              <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
            <p className="text-xs text-ink/50 max-w-[220px] leading-relaxed">
              Every quote is composed around your brief — quantities, branding and delivery.
            </p>
          </div>
        </Reveal>
      </div>
    </main>
  );
}
